import React, { useState } from 'react';
import {
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
} from 'react-icons/io5';
import { VscEye, VscEyeClosed } from 'react-icons/vsc';

import {
  Column,
  ErrorTag,
  ErrorText,
  Fields,
  FormBody,
  OutlineButton,
  PrimaryButton,
  TextInput,
  ToggleVisibility,
} from 'components/layout/Auth/AuthForm/styles';

import {
  passwordIsValid,
  passwordRules,
} from '../../EsqueciSenha/passwordRules';
import {
  CONTENT_WIDTH,
  NarrowActions,
  NarrowField,
  OnboardingIntro,
  OnboardingSubtitle,
  OnboardingTitle,
  RuleFail,
  RuleOk,
  RuleRow,
  RulesBlock,
  RulesList,
  RulesTitle,
} from '../styles';

type SenhaProps = {
  loading: boolean;
  erro: string | null;
  onSubmit: (senha: string) => void;
  onBack: () => void;
};

const Senha = ({
  loading,
  erro,
  onSubmit,
  onBack,
}: SenhaProps): JSX.Element => {
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [verConfirmacao, setVerConfirmacao] = useState(false);
  const [tocouConfirmacao, setTocouConfirmacao] = useState(false);

  const naoConferem =
    tocouConfirmacao && !!confirmacao && senha !== confirmacao;
  const podeEnviar =
    !loading &&
    passwordIsValid(senha) &&
    !!confirmacao &&
    senha === confirmacao;

  const olho = (visivel: boolean, alternar: () => void): React.ReactElement => (
    <ToggleVisibility
      type="button"
      onClick={alternar}
      aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
    >
      {visivel ? <VscEye /> : <VscEyeClosed />}
    </ToggleVisibility>
  );

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    setTocouConfirmacao(true);
    if (podeEnviar) onSubmit(senha);
  };

  return (
    <Column gap={24} width={CONTENT_WIDTH}>
      <OnboardingIntro>
        <OnboardingTitle>Para uma maior segurança</OnboardingTitle>
        <OnboardingSubtitle>
          Defina sua senha de acesso e garanta que ela siga todos os requisitos
        </OnboardingSubtitle>
      </OnboardingIntro>

      <form onSubmit={handleSubmit} noValidate>
        <FormBody>
          <Fields>
            <NarrowField>
              <TextInput
                id="senha"
                name="senha"
                type={verSenha ? 'text' : 'password'}
                label="Senha"
                placeholder="Digite"
                required
                fullWidth
                autoFocus
                value={senha}
                onChange={(event) => setSenha(event.target.value)}
                trailingIcon={olho(verSenha, () => setVerSenha((v) => !v))}
              />
            </NarrowField>

            <NarrowField>
              <TextInput
                id="confirmar-senha"
                name="confirmar-senha"
                type={verConfirmacao ? 'text' : 'password'}
                label="Confirme sua senha"
                placeholder="Digite"
                required
                fullWidth
                error={naoConferem}
                value={confirmacao}
                onChange={(event) => setConfirmacao(event.target.value)}
                onBlur={() => setTocouConfirmacao(true)}
                trailingIcon={olho(verConfirmacao, () =>
                  setVerConfirmacao((v) => !v)
                )}
              />
              {naoConferem && <ErrorText>As senhas não conferem.</ErrorText>}
            </NarrowField>
          </Fields>

          <RulesBlock>
            <RulesTitle>Sua senha deve conter:</RulesTitle>
            <RulesList>
              {passwordRules.map((rule) => {
                const ok = rule.test(senha);

                return (
                  <RuleRow key={rule.id}>
                    {ok ? (
                      <RuleOk>
                        <IoCheckmarkCircleOutline aria-hidden="true" />
                      </RuleOk>
                    ) : (
                      <RuleFail>
                        <IoCloseCircleOutline aria-hidden="true" />
                      </RuleFail>
                    )}
                    {rule.label}
                  </RuleRow>
                );
              })}
            </RulesList>
          </RulesBlock>

          {!!erro && <ErrorTag role="alert">{erro}</ErrorTag>}

          <NarrowActions>
            <OutlineButton
              type="button"
              variant="secondary"
              shape="pill"
              fullWidth
              onClick={onBack}
            >
              Voltar
            </OutlineButton>
            <PrimaryButton
              type="submit"
              shape="pill"
              fullWidth
              disabled={!podeEnviar}
            >
              {loading ? 'Aguarde...' : 'Continuar'}
            </PrimaryButton>
          </NarrowActions>
        </FormBody>
      </form>
    </Column>
  );
};

export default Senha;
